import { useFormContext } from 'react-hook-form';
import type { Flow } from '../types';
import { usePagination } from 'hooks/usePagination';
import { useFlows } from 'features/cabinet/tiktok/api';
import { BaseGrid, SuspenseBoundary } from 'components/common';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash-es';
import { useListParams } from 'hooks';
import { usePermissionGuard } from '../../../login';
import { useFlowsColumns } from '../hooks';

const FlowsGridComponent = memo(() => {
  const { accessToChangeTiktoktext } = usePermissionGuard();
  const navigate = useNavigate();
  const { watch } = useFormContext();
  const pagination = usePagination([], 50);
  const { setApps, apps: urlApps } = useListParams();
  const [debouncedFilters, setDebouncedFilters] = useState<{
    apps: string | undefined;
  }>({
    apps: urlApps?.join(',') ?? undefined,
  });

  const filterByApps = watch('apps') as string[];

  const debouncedUpdate = useCallback(
    (apps?: string[]) => {
      setDebouncedFilters({ apps: apps?.join(',') ?? undefined });
      setApps(apps ?? undefined);
    },
    [setDebouncedFilters, setApps],
  );

  const debouncedFn = useMemo(
    () => debounce(debouncedUpdate, 500),
    [debouncedUpdate],
  );

  useEffect(() => {
    debouncedFn(filterByApps);
    return () => {
      debouncedFn.cancel();
    };
  }, [filterByApps, debouncedFn]);

  const columns = useFlowsColumns();

  const {
    data: { data: flows, count },
    isLoading,
  } = useFlows({
    params: {
      apps: debouncedFilters.apps ?? undefined,
      desc: true,
      order_by: 'created',
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const handleClick = useCallback(
    (id: number) => {
      accessToChangeTiktoktext &&
      navigate(`/cabinet/tiktok/flows/${id}`);
    },
    [navigate, accessToChangeTiktoktext],
  );

  return (
    <BaseGrid<Flow>
      rows={flows ?? []}
      columns={columns}
      count={count}
      isLoading={isLoading}
      pagination={pagination}
      getRowId={(row) => row.id}
      onRowClick={(params) => {
        handleClick((params.row as Flow).id);
      }}
    />
  );
});

FlowsGridComponent.displayName = 'FlowsGridComponent';

const FlowsGrid = () => {
  return (
    <SuspenseBoundary>
      <FlowsGridComponent />
    </SuspenseBoundary>
  );
};

export default FlowsGrid;
